import { Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { GenericCrudAddComponent } from 'src/app/libs/components/generic-crud/generic-crud-add.component';
import {
  BatchCodeDefinitions,
  BatchSelectionCriteria,
  BatchSplittingMode,
  BatchTraceability,
  BatchType,
  EBatchCodeDefinition,
  EBatchTraceability,
  EProviderBatchCodeDefinition,
  ProviderBatchCodeDefinitions,
} from 'src/app/models';

@Component({
  selector: 'app-batch-types-add',
  templateUrl: './batch-types-add.component.html',
  styleUrl: './batch-types-add.component.sass',
  standalone: false,
})
export class BatchTypesAddComponent extends GenericCrudAddComponent<BatchType> {
  override resource = this.api.resources.product.batchTypes;
  productBatchStates = this.api.resources.product.productBatchStates;

  batchCodeDefinitions: { code: string; name: string }[] = [];
  providerBatchCodeDefinitions: { code: string; name: string }[] = [];
  batchSelectionCriteriaItems: { code: string; name: string }[] = [];
  batchTraceabilityItems: { code: string; name: string }[] = [];
  batchSplittingModeItems: { code: string; name: string }[] = [];

  protected override async preFetch(): Promise<void> {
    const keys = [
      ...BatchCodeDefinitions.map((x) => `enumeration.BatchCodeDefinition.${x.name}`),
      ...ProviderBatchCodeDefinitions.map((x) => `enumeration.ProviderBatchCodeDefinition.${x.name}`),
      ...BatchSelectionCriteria.map((x) => `enumeration.BatchSelectionCriteria.${x.name}`),
      ...BatchTraceability.map((x) => `enumeration.BatchTraceability.${x.name}`),
      ...BatchSplittingMode.map((x) => `enumeration.BatchSplittingMode.${x.name}`),
    ];
    await firstValueFrom(this.tr.get(keys));
    this.batchCodeDefinitions = BatchCodeDefinitions.map((x) => ({
      code: x.name,
      name: this.tr.instant(`enumeration.BatchCodeDefinition.${x.name}`),
    }));
    this.providerBatchCodeDefinitions = ProviderBatchCodeDefinitions.map((x) => ({
      code: x.name,
      name: this.tr.instant(`enumeration.ProviderBatchCodeDefinition.${x.name}`),
    }));
    this.batchSelectionCriteriaItems = BatchSelectionCriteria.map((x) => ({
      code: x.name,
      name: this.tr.instant(`enumeration.BatchSelectionCriteria.${x.name}`),
    }));
    this.batchTraceabilityItems = BatchTraceability.map((x) => ({
      code: x.name,
      name: this.tr.instant(`enumeration.BatchTraceability.${x.name}`),
    }));
    this.batchSplittingModeItems = BatchSplittingMode.map((x) => ({
      code: x.name,
      name: this.tr.instant(`enumeration.BatchSplittingMode.${x.name}`),
    }));
  }

  initFormGroup() {
    const form = this.fb.group({
      name: ['', Validators.required],
      companyId: [this.api.auth.userValue?.lastCompanyId],
      batchTraceability: [undefined, Validators.required],
      batchCodeDefinition: [undefined, Validators.required],
      providerBatchCodeDefinition: [undefined, Validators.required],
      batchSelectionCriteria: [undefined, Validators.required],
      batchSplittingMode: [null],
      defaultBatchStateId: [undefined, Validators.required],
      batchCodeRangeMin: [null],
      batchCodeRangeMax: [null],
      providerBatchCodeRangeMin: [null],
      providerBatchCodeRangeMax: [null],
      batchCodeUseEan13: [false],
      providerBatchCodeUseEan13: [false],
    });

    this.updateTraceabilityDependentValidators(form.value.batchTraceability || undefined);
    this.updateCodeDefinitionDependentFields(form.value.batchCodeDefinition || undefined);
    this.updateProviderCodeDefinitionDependentFields(form.value.providerBatchCodeDefinition || undefined);
    return form;
  }

  isBatch:boolean = false;
  isSerialNumber:boolean = false;
  isSequentialCode: boolean = false;
  isSequentialProviderCode: boolean = false;

  updateTraceabilityDependentValidators(value?: string) {
    if (!value)
      return;

    const batchTraceabilitySelected = BatchTraceability.find(_ => _.name === value)?.id;

    this.isBatch = batchTraceabilitySelected === EBatchTraceability.Batch;
    this.isSerialNumber = batchTraceabilitySelected === EBatchTraceability.SerialNumber;

    if (batchTraceabilitySelected === EBatchTraceability.Batch) {
      this.addValidation('batchCodeDefinition');
      this.addValidation('providerBatchCodeDefinition');
      this.addValidation('batchSelectionCriteria');
      this.addValidation('batchSplittingMode');
    }
    else if (batchTraceabilitySelected === EBatchTraceability.WithoutBatch) {
      this.removeValidation('batchCodeDefinition');
      this.removeValidation('providerBatchCodeDefinition');
      this.removeValidation('batchSelectionCriteria');
      this.removeValidation('batchSplittingMode');
      this.removeValidation('batchCodeRangeMin');
      this.removeValidation('batchCodeRangeMax');
      this.removeValidation('providerBatchCodeRangeMin');
      this.removeValidation('providerBatchCodeRangeMax');
    }
    else if (batchTraceabilitySelected === EBatchTraceability.SerialNumber) {
      this.addValidation('batchCodeDefinition');
      this.addValidation('providerBatchCodeDefinition');
      this.removeValidation('batchSelectionCriteria');
      this.addValidation('batchSplittingMode');
    }
  }

  private addValidation(control:string) {
    const formControl = this.form?.get(control);
    if (!formControl)
      return;

    formControl.setValidators([Validators.required]);
    formControl.updateValueAndValidity();
  }

  private removeValidation(control:string) {
    const formControl = this.form?.get(control);
    if (!formControl)
      return;

    formControl.clearValidators();
    formControl.setValue(undefined);
    formControl.updateValueAndValidity();
  }

  updateCodeDefinitionDependentFields(value?: string) {
    if (!value)
      return;

    const batchCodeDefinitionSelected = BatchCodeDefinitions.find(_ => _.name === value)?.id;
    this.isSequentialCode = batchCodeDefinitionSelected === EBatchCodeDefinition.Sequential ||
                               batchCodeDefinitionSelected === EBatchCodeDefinition.SequentialWithYearPrefix;

    if (this.isSequentialCode) {
      this.addValidation('batchCodeRangeMin');
      this.addValidation('batchCodeRangeMax');
    } else {
      this.removeValidation('batchCodeRangeMin');
      this.removeValidation('batchCodeRangeMax');
    }
  }

  updateProviderCodeDefinitionDependentFields(value?: string) {
    if (!value)
      return;

    const providerBatchCodeDefinitionSelected = ProviderBatchCodeDefinitions.find(_ => _.name === value)?.id;
    this.isSequentialProviderCode = providerBatchCodeDefinitionSelected === EProviderBatchCodeDefinition.Sequential ||
                                       providerBatchCodeDefinitionSelected === EProviderBatchCodeDefinition.SequentialWithYearPrefix;

    if (this.isSequentialProviderCode) {
      this.addValidation('providerBatchCodeRangeMin');
      this.addValidation('providerBatchCodeRangeMax');
    } else {
      this.removeValidation('providerBatchCodeRangeMin');
      this.removeValidation('providerBatchCodeRangeMax');
    }
  }
}
